if (!customElements.get('collection-filters')) {
    /**
     * Centralized Filter Manager
     * Coordinates all collection-filters instances to prevent duplicate requests
     * and keep all filters in sync
     */
    class CollectionFilterManager {
        constructor() {
            this.instances = new Set(); // All registered filter instances
            this.updateQueue = new Map(); // Queue of pending updates by section ID
            this.isUpdating = false; // Lock to prevent concurrent updates
            this.debounceTimer = null;
            this.debounceDelay = 300; // 300ms debounce
            this.pendingSections = new Set(); // Sections waiting to be updated
        }

        /**
         * Register a filter instance
         */
        register(instance) {
            this.instances.add(instance);
        }

        /**
         * Unregister a filter instance
         */
        unregister(instance) {
            this.instances.delete(instance);
        }

        /**
         * Request a section update (debounced and queued)
         */
        requestUpdate(instance) {
            // Determine which section(s) need updating
            const sectionsToQueue = new Set();

            // If instance has a targetSectionId, queue that section
            if (instance.targetSectionId) {
                sectionsToQueue.add(instance.targetSectionId);
            }

            // If instance doesn't have a targetSectionId, it's likely a filter panel itself
            // that needs to be updated to show new filter counts. Queue its own section.
            // We need to find the Shopify section ID from the instance's sectionId
            if (!instance.targetSectionId && instance.sectionId) {
                // The sectionId is the Shopify section ID, but we need to find the actual DOM element
                // to get the section wrapper ID. We'll queue it by finding the section wrapper.
                const instanceElement = instance;
                const sectionWrapper = instanceElement.closest('[id^="shopify-section-"]');
                if (sectionWrapper) {
                    // Extract the section ID from the wrapper (e.g., "template--25936626319737__group_7eqDye")
                    const wrapperSectionId = sectionWrapper.id.replace('shopify-section-', '');
                    sectionsToQueue.add(wrapperSectionId);
                } else {
                    // Fallback: use the sectionId directly if we can't find the wrapper
                    sectionsToQueue.add(instance.sectionId);
                }
            }

            // Also update all linked instances (same collection base URL) - they all need updates
            const linkedInstances = this.getLinkedInstances(instance);
            for (const linkedInstance of linkedInstances) {
                if (linkedInstance.targetSectionId) {
                    sectionsToQueue.add(linkedInstance.targetSectionId);
                }
                // Also queue the linked instance's own section if it doesn't have a target
                if (!linkedInstance.targetSectionId && linkedInstance.sectionId) {
                    const linkedElement = linkedInstance;
                    const linkedSectionWrapper = linkedElement.closest('[id^="shopify-section-"]');
                    if (linkedSectionWrapper) {
                        const linkedWrapperSectionId = linkedSectionWrapper.id.replace('shopify-section-', '');
                        sectionsToQueue.add(linkedWrapperSectionId);
                    } else {
                        sectionsToQueue.add(linkedInstance.sectionId);
                    }
                }
            }

            if (sectionsToQueue.size === 0) {
                return;
            }

            // Add all sections to pending
            sectionsToQueue.forEach(sectionId => {
                this.pendingSections.add(sectionId);
            });

            // Show loading immediately (before debounce) for responsive feedback
            this.showLoadingForSections(Array.from(this.pendingSections));

            // Clear existing debounce timer
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            // Debounce the update
            this.debounceTimer = setTimeout(() => {
                this.processUpdates();
            }, this.debounceDelay);
        }

        /**
         * Show loading overlay immediately for given section IDs
         */
        showLoadingForSections(sectionIds) {
            const affectedInstances = Array.from(this.instances).filter(inst => {
                if (inst.targetSectionId && sectionIds.includes(inst.targetSectionId)) {
                    return true;
                }
                if (inst.sectionId && sectionIds.includes(inst.sectionId)) {
                    return true;
                }
                const instanceElement = inst;
                const sectionWrapper = instanceElement.closest('[id^="shopify-section-"]');
                if (sectionWrapper) {
                    const wrapperSectionId = sectionWrapper.id.replace('shopify-section-', '');
                    if (sectionIds.includes(wrapperSectionId)) {
                        return true;
                    }
                }
                return false;
            });
            affectedInstances.forEach(inst => inst.showLoading());
        }

        /**
         * Process all pending updates
         */
        async processUpdates() {
            // If already updating, wait for current update to complete
            if (this.isUpdating) {
                // Re-queue after a short delay
                setTimeout(() => this.processUpdates(), 100);
                return;
            }

            if (this.pendingSections.size === 0) {
                return;
            }

            // Set lock
            this.isUpdating = true;

            // Collect all unique section IDs that need updating
            const sectionsToUpdate = Array.from(this.pendingSections);
            this.pendingSections.clear();

            // Ensure loading states are shown (may already be visible from requestUpdate)
            this.showLoadingForSections(sectionsToUpdate);

            try {
                // Get current URL parameters
                const urlParams = new URLSearchParams(window.location.search);
                const sectionUrl = `${window.location.pathname}?${urlParams.toString()}`;

                // Group instances by section ID
                const instancesBySection = new Map();

                // For each section that needs updating, find all instances that should be updated
                for (const sectionId of sectionsToUpdate) {
                    // Find instances that target this section
                    for (const instance of this.instances) {
                        // Case 1: Instance has this as its targetSectionId
                        if (instance.targetSectionId === sectionId) {
                            if (!instancesBySection.has(sectionId)) {
                                instancesBySection.set(sectionId, []);
                            }
                            instancesBySection.get(sectionId).push(instance);
                        }
                        // Case 2: Instance doesn't have a targetSectionId, but its own section matches
                        // (this is a filter panel that needs to update itself)
                        else if (!instance.targetSectionId && instance.sectionId === sectionId) {
                            if (!instancesBySection.has(sectionId)) {
                                instancesBySection.set(sectionId, []);
                            }
                            instancesBySection.get(sectionId).push(instance);
                        }
                        // Case 3: Find by section wrapper ID
                        else if (!instance.targetSectionId) {
                            const instanceElement = instance;
                            const sectionWrapper = instanceElement.closest('[id^="shopify-section-"]');
                            if (sectionWrapper) {
                                const wrapperSectionId = sectionWrapper.id.replace('shopify-section-', '');
                                if (wrapperSectionId === sectionId) {
                                    if (!instancesBySection.has(sectionId)) {
                                        instancesBySection.set(sectionId, []);
                                    }
                                    instancesBySection.get(sectionId).push(instance);
                                }
                            }
                        }
                    }
                }

                // Fetch all sections in parallel
                const fetchPromises = Array.from(instancesBySection.keys()).map(async (sectionId) => {
                    // Find the first instance for this section to get the ancestor
                    const firstInstance = instancesBySection.get(sectionId)[0];
                    let targetSection = null;
                    let ancestorSection = null;
                    let shopifySectionId = sectionId;

                    // Try to find the section element
                    // First, check if sectionId is a targetSectionId (element ID or data-section-id)
                    targetSection = document.getElementById(sectionId);
                    if (!targetSection) {
                        targetSection = document.querySelector(`[data-section-id="${sectionId}"]`);
                    }

                    // If not found, check if sectionId is a Shopify section ID (like template--25936626319737__group_7eqDye)
                    // and find the wrapper element
                    if (!targetSection) {
                        const sectionWrapper = document.querySelector(`[id="shopify-section-${sectionId}"]`);
                        if (sectionWrapper) {
                            ancestorSection = sectionWrapper;
                            shopifySectionId = sectionId; // Already the Shopify section ID
                        }
                    } else {
                        // Found targetSection, now find its ancestor wrapper
                        ancestorSection = targetSection.closest('[id^="shopify-section-template--"]');
                        if (ancestorSection) {
                            shopifySectionId = ancestorSection.id.replace('shopify-section-', '');
                        }
                    }

                    // If still not found, try using the first instance's section wrapper
                    if (!ancestorSection && firstInstance) {
                        const instanceElement = firstInstance;
                        ancestorSection = instanceElement.closest('[id^="shopify-section-template--"]');
                        if (ancestorSection) {
                            shopifySectionId = ancestorSection.id.replace('shopify-section-', '');
                        }
                    }

                    if (!ancestorSection) {
                        return { sectionId, html: null };
                    }

                    const fetchUrl = `${sectionUrl}&sections=${shopifySectionId}`;

                    try {
                        const response = await fetch(fetchUrl);
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const data = await response.json();
                        return { 
                            sectionId, 
                            shopifySectionId,
                            html: data[shopifySectionId],
                            ancestorSection,
                            instances: instancesBySection.get(sectionId)
                        };
                    } catch (error) {
                        return { sectionId, html: null, instances: instancesBySection.get(sectionId) };
                    }
                });

                // Wait for all fetches to complete
                const results = await Promise.all(fetchPromises);

                // Update all sections
                for (const result of results) {
                    if (!result.html || !result.instances) {
                        continue;
                    }

                    // Update DOM for this section
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = result.html;
                    const newSectionElement = tempDiv.querySelector(`[id^="shopify-section-template--"]`);

                    if (newSectionElement && result.ancestorSection) {
                        // Replace the section
                        result.ancestorSection.parentNode.replaceChild(newSectionElement, result.ancestorSection);

                        // Update all instances that target this section
                        for (const instance of result.instances) {
                            // Update disableEmptyFilters flag
                            const newCollectionFilters = newSectionElement.querySelector('collection-filters');
                            if (newCollectionFilters) {
                                instance.disableEmptyFilters = newCollectionFilters.dataset.disableEmptyFilters === 'true';
                            }

                            // Re-initialize filters
                            instance.initializeFilters();
                            instance.updateFiltersFromURL();
                            instance.updateActiveValuesCount();
                            instance.updateEmptyFilterStates();
                        }

                        // Dispatch section update event
                        const sectionUpdateEvent = new CustomEvent('section-updated', {
                            detail: {
                                sectionId: result.shopifySectionId,
                                type: 'collection-filters',
                                url: sectionUrl
                            },
                            bubbles: true
                        });
                        document.dispatchEvent(sectionUpdateEvent);
                        
                        // Sort by discount if needed (client-side sort)
                        this.sortSectionByDiscount(newSectionElement);
                    }
                }

                // Update filter link active states for all instances
                requestAnimationFrame(() => {
                    for (const instance of this.instances) {
                        instance.updateFilterLinkActiveStates();
                    }
                });

            } catch (error) {
                console.error('Error in CollectionFilterManager.processUpdates:', error);
            } finally {
                // Wait for the browser to paint the new content before hiding loading
                // Double rAF ensures the DOM changes have been rendered to screen
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Hide loading for all instances
                        for (const inst of this.instances) {
                            inst.hideLoading();
                        }
                        // Release lock
                        this.isUpdating = false;
                    });
                });
            }
        }

        /**
         * Sort product cards by discount amount value
         */
        sortSectionByDiscount(sectionElement) {
            if (!sectionElement) return;

            // Check if sort_by is discount-descending
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('sort_by') !== 'discount-descending') return;

            // Find product cards with data-card-tiers attribute
            const productCards = Array.from(sectionElement.querySelectorAll('product-card[data-card-tiers]'));
            if (productCards.length === 0) return;

            // Group by parent to handle potential multiple grids
            const cardsByParent = new Map();
            
            productCards.forEach(card => {
                const parent = card.parentElement;
                if (!cardsByParent.has(parent)) {
                    cardsByParent.set(parent, []);
                }
                cardsByParent.get(parent).push(card);
            });

            // Sort within each parent
            cardsByParent.forEach((cards, parent) => {
                const cardsWithDiscount = cards.map(card => {
                    let discount = 0;
                    try {
                        const tiersData = card.getAttribute('data-card-tiers');
                        if (tiersData) {
                            const tiers = JSON.parse(tiersData);
                            // Find min discount amount value
                            if (Array.isArray(tiers) && tiers.length > 0) {
                                discount = tiers.reduce((min, tier) => {
                                    const val = parseFloat(tier.discount_amount_value) || 0;
                                    return val < min ? val : min;
                                }, parseFloat(tiers[0].discount_amount_value) || 0);
                            }
                        }
                    } catch (e) {
                        // console.warn('Error parsing card tiers:', e);
                    }
                    return { card, discount };
                });

                // Sort descending
                cardsWithDiscount.sort((a, b) => b.discount - a.discount);

                // Re-append in order
                const fragment = document.createDocumentFragment();
                cardsWithDiscount.forEach(({ card }) => {
                    fragment.appendChild(card);
                });
                
                parent.appendChild(fragment);
            });
        }

        /**
         * Get all instances that share the same collection base URL
         */
        getLinkedInstances(instance) {
            return Array.from(this.instances).filter(inst => 
                inst !== instance && 
                inst.collectionBaseUrl === instance.collectionBaseUrl &&
                inst.isInitialized
            );
        }
    }

    // Create singleton instance
    const filterManager = new CollectionFilterManager();

    class CollectionFilters extends HTMLElement {
        constructor() {
            super();
            this.sectionId = null;
            this.targetSectionId = null;
            this.resultsUrl = null;
            this.productsCount = 0;
            this.totalActiveValues = 0;
            this.filters = [];
            this.sortBy = null;
            this.isInitialized = false;
            this.isApplyingFiltersFromURL = false; // Flag to prevent recursive URL updates
            this.disableEmptyFilters = false; // Flag for disabling empty filters
        }

        connectedCallback() {
            this.initialize();
            this.setupEventListeners();
            // Register with the centralized manager
            filterManager.register(this);
        }

        disconnectedCallback() {
            this.cleanup();
            // Unregister from the centralized manager
            filterManager.unregister(this);
        }

        cleanup() {
            // Remove event listeners if handlers were created
            if (this._boundHandlers) {
                this.removeEventListener('change', this._boundHandlers.filterChange);
                this.removeEventListener('submit', this._boundHandlers.formSubmit);
                document.removeEventListener('keydown', this._boundHandlers.keyDown);
                window.removeEventListener('popstate', this._boundHandlers.urlChange);
                document.removeEventListener('url-change', this._boundHandlers.urlChangeEvent);
                window.removeEventListener('url-change', this._boundHandlers.urlChangeEvent);
                document.removeEventListener('click', this._boundHandlers.linkClick, true);
                this._boundHandlers = null;
            }
        }

        initialize() {
            if (this.isInitialized) return;

            this.sectionId = this.dataset.sectionId;
            this.targetSectionId = this.dataset.targetSectionId || null;
            this.resultsUrl = this.dataset.resultsUrl;
            this.productsCount = parseInt(this.dataset.productsCount) || 0;
            this.totalActiveValues = parseInt(this.dataset.totalActiveValues) || 0;
            this.disableEmptyFilters = this.dataset.disableEmptyFilters === 'true';
            
            // Get collection base URL (pathname without query parameters)
            this.collectionBaseUrl = window.location.pathname;
            
            if (this.targetSectionId) {
                const targetSectionById = document.getElementById(this.targetSectionId);
                const targetSectionByData = document.querySelector(`[data-section-id="${this.targetSectionId}"]`);
                
                if (!targetSectionById && !targetSectionByData) {
                    console.warn('⚠️ CollectionFilters: Target section not found!', this.targetSectionId);
                }
            }

            // Initialize filters
            this.initializeFilters();
            
            // Sync filters from URL if there are parameters (for initial page load)
            if (window.location.search) {
                this.updateFiltersFromURL();
            } else {
                // Update "View All" states even if there are no URL parameters
                this.updateAllViewAllStates();
                // Update empty filter states on initial load
                this.updateEmptyFilterStates();
            }
            
            // Setup collection filter link interception
            this.setupCollectionFilterLinks();
            
            // Update active states for filter links after a brief delay to ensure DOM is ready
            // This handles cases where links might be loaded dynamically
            requestAnimationFrame(() => {
                this.updateFilterLinkActiveStates();
                
                // Sort by discount on load if needed
                if (this.targetSectionId) {
                    const targetSection = document.getElementById(this.targetSectionId) || 
                                          document.querySelector(`[data-section-id="${this.targetSectionId}"]`);
                    if (targetSection) {
                        const ancestorSection = targetSection.closest('[id^="shopify-section-"]');
                        if (ancestorSection) {
                            filterManager.sortSectionByDiscount(ancestorSection);
                        }
                    }
                } else if (this.sectionId) {
                    // Fallback to own section
                    const ancestorSection = this.closest('[id^="shopify-section-"]');
                    if (ancestorSection) {
                        filterManager.sortSectionByDiscount(ancestorSection);
                    }
                }
            });
            
            this.isInitialized = true;
        }

        setupEventListeners() {
            // Create bound handlers for proper cleanup
            this._boundHandlers = {
                filterChange: this.handleFilterChange.bind(this),
                formSubmit: this.handleFormSubmit.bind(this),
                keyDown: this.handleKeyDown.bind(this),
                urlChange: this.handleUrlChange.bind(this),
                urlChangeEvent: (e) => {
                    if (e.detail.type === 'collection-filter') {
                        this.handleUrlChange(e);
                    }
                },
                linkClick: this.handleCollectionFilterLinkClick.bind(this)
            };
            
            // Listen for filter changes
            this.addEventListener('change', this._boundHandlers.filterChange);
            
            // Listen for form submissions
            this.addEventListener('submit', this._boundHandlers.formSubmit);
            
            // Listen for keyboard navigation
            document.addEventListener('keydown', this._boundHandlers.keyDown);
            
            // Listen for URL changes (browser back/forward navigation)
            window.addEventListener('popstate', this._boundHandlers.urlChange);
            
            // Listen for standardized URL change events from other collection-filters elements
            document.addEventListener('url-change', this._boundHandlers.urlChangeEvent);
            window.addEventListener('url-change', this._boundHandlers.urlChangeEvent);
            
            // Listen for clicks on collection filter links
            document.addEventListener('click', this._boundHandlers.linkClick, true);
        }

        initializeFilters() {
            // Get all filter inputs
            const filterInputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"], input[type="number"]');
            
            this.filters = Array.from(filterInputs).map(input => ({
                element: input,
                name: input.name,
                value: input.value,
                type: input.type,
                checked: input.checked
            }));

            // Initialize sort by
            const sortSelect = this.querySelector('select[name="sort_by"]');
            if (sortSelect) {
                this.sortBy = sortSelect.value;
            }
        }

        handleFilterChange(event) {
            // Skip URL updates if we're applying filters from a URL (to prevent recursive updates)
            if (this.isApplyingFiltersFromURL) {
                return;
            }
            
            const target = event.target;
            
            // Handle different types of filter inputs
            if (target.type === 'checkbox' || target.type === 'radio') {
                this.handleCheckboxRadioChange(target);
            } else if (target.type === 'number') {
                this.handleNumberChange(target);
            } else if (target.name === 'sort_by') {
                this.handleSortChange(target);
            }

            // Update active values count
            this.updateActiveValuesCount();
            
            // Update URL parameters
            this.updateURLParameters();
            
            // Request update through centralized manager (debounced)
            filterManager.requestUpdate(this);
            
            // Update active states for filter links after a brief delay to ensure URL is updated
            requestAnimationFrame(() => {
                this.updateFilterLinkActiveStates();
            });
        }

        handleUrlChange(event) {
            // Update filter states based on current URL
            this.updateFiltersFromURL();
            
            // Update active values count
            this.updateActiveValuesCount();
            
            // Update active states for filter links
            this.updateFilterLinkActiveStates();
            
            // Trigger section update
            this.updateSection();
        }

        handleCheckboxRadioChange(input) {
            // Check if this is a "View All" checkbox
            const isViewAll = input.hasAttribute('data-filter-view-all');
            const filterName = input.dataset.filterName || input.name;

            if (isViewAll && input.checked) {
                // When "View All" is checked, clear all other checkboxes for this filter
                const filterValueInputs = this.querySelectorAll(
                    `input[data-filter-value][data-filter-name="${filterName}"]`
                );
                filterValueInputs.forEach(filterInput => {
                    filterInput.checked = false;
                    // Update the filter in the filters array
                    const filter = this.filters.find(f => f.element === filterInput);
                    if (filter) {
                        filter.checked = false;
                    }
                });
            } else if (!isViewAll && input.checked) {
                // When a filter value is checked, uncheck "View All" for this filter
                const viewAllInput = this.querySelector(
                    `input[data-filter-view-all][data-filter-name="${filterName}"]`
                );
                if (viewAllInput) {
                    viewAllInput.checked = false;
                    // Update the filter in the filters array
                    const filter = this.filters.find(f => f.element === viewAllInput);
                    if (filter) {
                        filter.checked = false;
                    }
                }
            }

            // Update the filter state
            const filter = this.filters.find(f => f.element === input);
            if (filter) {
                filter.checked = input.checked;
                filter.value = input.value;
            }

            // Sync ALL matching inputs with the same name and value across the component
            // This ensures desktop and mobile forms stay in sync
            // Skip syncing for "View All" checkboxes with empty values
            if (!isViewAll || input.value !== '') {
                const allMatchingInputs = this.querySelectorAll(`input[name="${input.name}"][value="${CSS.escape(input.value)}"]`);
                allMatchingInputs.forEach(matchingInput => {
                    if (matchingInput !== input) {
                        matchingInput.checked = input.checked;
                    }
                });
            }

            // Update "View All" state based on active filter values
            this.updateViewAllState(filterName);

            // Update URL parameters
            this.updateURLParameters();
        }

        handleNumberChange(input) {
            // Handle price range inputs
            const minInput = this.querySelector('input[name*="price.gte"]');
            const maxInput = this.querySelector('input[name*="price.lte"]');
            
            if (minInput && maxInput) {
                // Validate price range
                this.validatePriceRange(minInput, maxInput);
            }

            // Update URL parameters
            this.updateURLParameters();
        }

        handleSortChange(select) {
            this.sortBy = select.value;
            
            // Update URL parameters
            this.updateURLParameters();
            
            // Request update through centralized manager (debounced)
            filterManager.requestUpdate(this);
        }

        handleFormSubmit(event) {
            event.preventDefault();
            this.updateFilters();
        }

        handleKeyDown(event) {
            // Handle keyboard navigation for filters
            if (event.target.closest('collection-filters') !== this) return;

            const { key, target } = event;
            
            if (key === 'Enter' || key === ' ') {
                if (target.type === 'checkbox' || target.type === 'radio') {
                    event.preventDefault();
                    target.checked = !target.checked;
                    this.handleCheckboxRadioChange(target);
                }
            }
        }

        validatePriceRange(minInput, maxInput) {
            const minValue = parseFloat(minInput.value) || 0;
            const maxValue = parseFloat(maxInput.value) || Infinity;
            
            const minLimit = parseFloat(minInput.getAttribute('data-min')) || 0;
            const maxLimit = parseFloat(maxInput.getAttribute('data-max')) || Infinity;

            // Ensure min is not greater than max
            if (minValue > maxValue) {
                minInput.value = maxValue;
            }

            // Ensure values are within limits
            if (minValue < minLimit) {
                minInput.value = minLimit;
            }
            if (maxValue > maxLimit) {
                maxInput.value = maxLimit;
            }
        }

        updateURLParameters() {
            // Create form data from current state
            const formData = new FormData();
            
            // Add active filters (exclude "View All" checkboxes)
            this.filters.forEach(filter => {
                // Skip "View All" checkboxes (those with data-filter-view-all attribute or empty values)
                const isViewAll = filter.element.hasAttribute('data-filter-view-all') || 
                                 (filter.type === 'checkbox' && filter.value === '');
                if (isViewAll) {
                    return;
                }

                if (filter.checked && filter.type === 'checkbox') {
                    formData.append(filter.name, filter.value);
                } else if (filter.type === 'radio' && filter.element.checked) {
                    formData.append(filter.name, filter.value);
                } else if (filter.type === 'number' && filter.element.value) {
                    formData.append(filter.name, filter.element.value);
                }
            });

            // Add sort by
            if (this.sortBy) {
                formData.append('sort_by', this.sortBy);
            }

            // Update URL hash
            this.updateURLHash(formData);
        }

        updateURLHash(formData) {
            const url = new URL(window.location.href);
            
            // Start with existing URL parameters to preserve filters from other form elements
            const urlParameters = new URLSearchParams(url.search);
            
            // Get list of filter names managed by THIS form element
            const thisFormFilterNames = new Set();
            this.filters.forEach(filter => {
                thisFormFilterNames.add(filter.name);
            });
            // Also include sort_by if this form manages it (has a sort select)
            const sortSelect = this.querySelector('select[name="sort_by"]');
            if (sortSelect) {
                thisFormFilterNames.add('sort_by');
            }
            
            // Remove only the parameters that belong to THIS form element
            // This preserves filters from other collection-filters elements
            for (const filterName of thisFormFilterNames) {
                urlParameters.delete(filterName);
            }
            urlParameters.delete('page'); // Always remove page parameter for new filters
            
            // Add the current form's active filters with deduplication
            // Convert FormData to URLSearchParams to get values, then deduplicate
            const formDataParams = new URLSearchParams(formData);
            
            // Track unique values per parameter to avoid duplicates
            const uniqueParams = new Map();
            
            for (const param of formDataParams.keys()) {
                const values = formDataParams.getAll(param);
                // Use Set to automatically deduplicate values
                const uniqueValues = new Set(values.filter(v => v)); // Filter out empty values
                
                // Store unique values for this parameter
                uniqueParams.set(param, Array.from(uniqueValues));
            }
            
            // Add unique values to URL parameters
            // Note: We've already deleted parameters with these names, so we can safely add without checking for duplicates
            for (const [param, values] of uniqueParams.entries()) {
                // For parameters that can have multiple values (like checkboxes), add each unique value
                values.forEach(value => {
                    urlParameters.append(param, value);
                });
            }

            // Update URL with merged parameters
            const paramsString = urlParameters.toString();
            url.search = paramsString;
            const urlString = url.toString();

            // Dispatch standardized URL change event (handles pushState internally)
            if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
                window.theme.utils.dispatchUrlChange('collection-filter', { 
                    url: urlString,
                    urlParameters: paramsString,
                    state: { urlParameters: paramsString },
                    historyAction: 'replace'
                });
            }
        }

        clearURLParameters() {
            const url = new URL(window.location.href);
            
            // Remove all filter and sort parameters
            const paramsToRemove = [];
            for (const [key] of url.searchParams.entries()) {
                if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
                    paramsToRemove.push(key);
                }
            }
            
            paramsToRemove.forEach(param => {
                url.searchParams.delete(param);
            });
            
            const urlString = url.toString();
            
            // Dispatch standardized URL change event (handles pushState internally)
            if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
                window.theme.utils.dispatchUrlChange('collection-filter', { 
                    url: urlString,
                    cleared: true,
                    state: { urlParameters: '' },
                    historyAction: 'replace'
                });
            }
        }

        updateFiltersFromURL() {
            const urlParams = new URLSearchParams(window.location.search);
            
            // Update ALL matching inputs in the component (not just the ones in filters array)
            // This ensures both desktop and mobile forms stay in sync
            
            // Get all filter inputs in the component
            const allFilterInputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"], input[type="number"]');
            
            allFilterInputs.forEach(input => {
                if (input.type === 'checkbox') {
                    // For checkboxes, check if this value exists in the URL parameters
                    const urlValues = urlParams.getAll(input.name);
                    input.checked = urlValues.includes(input.value);
                } else if (input.type === 'radio') {
                    // For radio buttons, get the single value
                    const urlValue = urlParams.get(input.name);
                    input.checked = urlValue === input.value;
                } else if (input.type === 'number') {
                    // For number inputs, get the single value
                    const urlValue = urlParams.get(input.name);
                    input.value = urlValue || '';
                }
            });
            
            // Now update the filters array to match
            this.filters.forEach(filter => {
                if (filter.type === 'checkbox') {
                    // For checkboxes, check if this value exists in the URL parameters
                    const urlValues = urlParams.getAll(filter.name);
                    filter.element.checked = urlValues.includes(filter.value);
                    filter.checked = filter.element.checked;
                } else if (filter.type === 'radio') {
                    // For radio buttons, get the single value
                    const urlValue = urlParams.get(filter.name);
                    filter.element.checked = urlValue === filter.value;
                    filter.checked = filter.element.checked;
                } else if (filter.type === 'number') {
                    // For number inputs, get the single value
                    const urlValue = urlParams.get(filter.name);
                    filter.element.value = urlValue || '';
                    filter.value = filter.element.value;
                }
            });
            
            // Update sort by from URL - update ALL select elements
            const sortBy = urlParams.get('sort_by');
            if (sortBy) {
                this.sortBy = sortBy;
                // Update all sort selects
                const sortSelects = this.querySelectorAll('select[name="sort_by"]');
                sortSelects.forEach(sortSelect => {
                    sortSelect.value = sortBy;
                });
            }

            // Update "View All" checkboxes based on current filter state
            this.updateAllViewAllStates();
            
            // Update empty filter states after URL sync
            this.updateEmptyFilterStates();
        }

        updateFilters() {
            // Dispatch filter update event
            const filterUpdateEvent = new CustomEvent('filter-update', {
                detail: {
                    sectionId: this.sectionId,
                    filters: this.getActiveFilters(),
                    sortBy: this.sortBy,
                    productsCount: this.productsCount
                },
                bubbles: true
            });

            this.dispatchEvent(filterUpdateEvent);

            // Request update through centralized manager (debounced)
            filterManager.requestUpdate(this);
        }

        /**
         * @deprecated Use filterManager.requestUpdate() instead
         * Kept for backward compatibility but now delegates to centralized manager
         */
        async updateSection() {
            // Delegate to centralized manager
            filterManager.requestUpdate(this);
        }

        showLoading() {
            // Show loading for all linked instances (same collection base URL)
            const linkedInstances = filterManager.getLinkedInstances(this);
            const allInstances = [this, ...linkedInstances];
            
            for (const instance of allInstances) {
                if (!instance.targetSectionId) continue;
                
                let targetSection = document.getElementById(instance.targetSectionId);
                if (!targetSection) {
                    targetSection = document.querySelector(`[data-section-id="${instance.targetSectionId}"]`);
                }
                
                if (!targetSection) continue;
                
                // Find the ancestor section
                const ancestorSection = targetSection.closest('[id^="shopify-section-template--"]');
                if (!ancestorSection) continue;
                
                // Ensure ancestor section has relative positioning
                const computedStyle = window.getComputedStyle(ancestorSection);
                if (computedStyle.position === 'static') {
                    ancestorSection.style.position = 'relative';
                }
                
                // Create or get loading overlay
                let loadingOverlay = ancestorSection.querySelector('.collection-filters-loading');
                
                if (!loadingOverlay) {
                    loadingOverlay = document.createElement('div');
                    loadingOverlay.className = 'collection-filters-loading absolute inset-0 bg-white/90 flex items-center justify-center z-10 backdrop-blur-sm hidden';
                    loadingOverlay.innerHTML = `
                        <div class="flex flex-col items-center gap-4">
                            <div class="w-10 h-10 border-[3px] border-black/10 border-t-black rounded-full animate-spin"></div>
                            <p class="text-sm font-medium text-black m-0">Loading products...</p>
                        </div>
                    `;
                    ancestorSection.appendChild(loadingOverlay);
                }
                
                loadingOverlay.classList.remove('hidden');
            }
        }

        hideLoading() {
            // Hide loading for all linked instances (same collection base URL)
            const linkedInstances = filterManager.getLinkedInstances(this);
            const allInstances = [this, ...linkedInstances];
            
            for (const instance of allInstances) {
                if (!instance.targetSectionId) continue;
                
                let targetSection = document.getElementById(instance.targetSectionId);
                if (!targetSection) {
                    targetSection = document.querySelector(`[data-section-id="${instance.targetSectionId}"]`);
                }
                
                if (!targetSection) continue;
                
                // Find the ancestor section
                const ancestorSection = targetSection.closest('[id^="shopify-section-template--"]');
                if (!ancestorSection) continue;
                
                const loadingOverlay = ancestorSection.querySelector('.collection-filters-loading');
                if (loadingOverlay) {
                    loadingOverlay.classList.add('hidden');
                }
            }
        }

        updateActiveValuesCount() {
            let count = 0;
            
            this.filters.forEach(filter => {
                // Skip "View All" checkboxes (those with data-filter-view-all attribute or empty values)
                const isViewAll = filter.element.hasAttribute('data-filter-view-all') || 
                                 (filter.type === 'checkbox' && filter.value === '');
                if (isViewAll) {
                    return;
                }

                if (filter.type === 'checkbox' && filter.checked) {
                    count++;
                } else if (filter.type === 'radio' && filter.element.checked) {
                    count++;
                } else if (filter.type === 'number' && filter.element.value) {
                    count++;
                }
            });

            this.totalActiveValues = count;
            this.updateActiveValuesDisplay();
        }

        updateActiveValuesDisplay() {
            // Update filter count bubbles
            const bubbles = this.querySelectorAll('.filter-count-bubble__text, .facets__bubble');
            bubbles.forEach(bubble => {
                bubble.textContent = this.totalActiveValues;
            });

            // Show/hide clear all button
            const clearAllButton = this.querySelector('.facets__clear-all');
            if (clearAllButton) {
                clearAllButton.style.display = this.totalActiveValues > 0 ? 'block' : 'none';
            }

            // Update active states for filter options
            this.updateFilterActiveStates();
        }

        updateFilterActiveStates() {
            // With Tailwind group utilities, styling is now handled automatically by CSS
            // No manual DOM manipulation needed for visual states
        }

        updateViewAllState(filterName) {
            // Update "View All" checkbox state for a specific filter
            if (!filterName) return;

            const viewAllInput = this.querySelector(
                `input[data-filter-view-all][data-filter-name="${filterName}"]`
            );
            if (!viewAllInput) return;

            // Check if any filter values are active for this filter
            const filterValueInputs = this.querySelectorAll(
                `input[data-filter-value][data-filter-name="${filterName}"]`
            );
            
            const hasActiveValues = Array.from(filterValueInputs).some(input => input.checked);
            
            // Update "View All" checkbox state
            viewAllInput.checked = !hasActiveValues;
            
            // Update the filter in the filters array
            const filter = this.filters.find(f => f.element === viewAllInput);
            if (filter) {
                filter.checked = viewAllInput.checked;
            }
        }

        updateAllViewAllStates() {
            // Update all "View All" checkboxes based on current filter state
            const viewAllInputs = this.querySelectorAll('input[data-filter-view-all]');
            viewAllInputs.forEach(viewAllInput => {
                const filterName = viewAllInput.dataset.filterName;
                if (filterName) {
                    this.updateViewAllState(filterName);
                }
            });
        }

        /**
         * Update disabled states and opacity for filter values with count 0
         * This is called after section updates to handle dynamically loaded filter counts
         */
        updateEmptyFilterStates() {
            if (!this.disableEmptyFilters) {
                // If the feature is disabled, ensure all filters are enabled
                const allFilterInputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"]');
                allFilterInputs.forEach(input => {
                    input.removeAttribute('disabled');
                    const label = input.closest('label');
                    if (label) {
                        label.classList.remove('opacity-25');
                    }
                });
                return;
            }

            // Get all filter inputs that have a data-filter-count attribute
            const filterInputs = this.querySelectorAll('input[type="checkbox"][data-filter-count], input[type="radio"][data-filter-count]');
            
            filterInputs.forEach(input => {
                const count = parseInt(input.getAttribute('data-filter-count')) || 0;
                const label = input.closest('label');
                
                if (count === 0) {
                    // Disable the input and add opacity to the label
                    input.setAttribute('disabled', 'disabled');
                    if (label) {
                        label.classList.add('opacity-25');
                    }
                } else {
                    // Enable the input and remove opacity from the label
                    input.removeAttribute('disabled');
                    if (label) {
                        label.classList.remove('opacity-25');
                    }
                }
            });
        }

        getActiveFilters() {
            const activeFilters = {};
            
            this.filters.forEach(filter => {
                if (filter.type === 'checkbox' && filter.checked) {
                    if (!activeFilters[filter.name]) {
                        activeFilters[filter.name] = [];
                    }
                    activeFilters[filter.name].push(filter.value);
                } else if (filter.type === 'radio' && filter.element.checked) {
                    activeFilters[filter.name] = filter.value;
                } else if (filter.type === 'number' && filter.element.value) {
                    activeFilters[filter.name] = filter.element.value;
                }
            });

            return activeFilters;
        }

        /**
         * Get filter parameters from a URL (excluding sort_by)
         * Returns a normalized object with filter parameters
         */
        getFilterParamsFromURL(urlString) {
            try {
                const url = new URL(urlString, window.location.origin);
                const params = new URLSearchParams(url.search);
                const filterParams = {};
                
                // Extract all filter parameters (excluding sort_by and page)
                for (const [key, value] of params.entries()) {
                    if (key.startsWith('filter.') && value) {
                        if (!filterParams[key]) {
                            filterParams[key] = [];
                        }
                        filterParams[key].push(value);
                    }
                }
                
                // Normalize: convert single-value arrays to sorted arrays for comparison
                const normalized = {};
                for (const [key, values] of Object.entries(filterParams)) {
                    normalized[key] = Array.from(new Set(values)).sort();
                }
                
                return normalized;
            } catch (e) {
                console.warn('Error parsing URL for filter params:', e);
                return {};
            }
        }

        /**
         * Compare two filter parameter objects (ignoring sort_by)
         * Returns true if they match
         */
        compareFilterParams(params1, params2) {
            const keys1 = Object.keys(params1).sort();
            const keys2 = Object.keys(params2).sort();
            
            if (keys1.length !== keys2.length) {
                return false;
            }
            
            for (const key of keys1) {
                if (!params2[key]) {
                    return false;
                }
                
                const values1 = Array.from(new Set(params1[key])).sort();
                const values2 = Array.from(new Set(params2[key])).sort();
                
                if (values1.length !== values2.length) {
                    return false;
                }
                
                for (let i = 0; i < values1.length; i++) {
                    if (values1[i] !== values2[i]) {
                        return false;
                    }
                }
            }
            
            return true;
        }

        /**
         * Setup collection filter links - mark links that point to the same collection
         * with filter parameters for interception
         */
        setupCollectionFilterLinks() {
            // This method is called during initialization
            // Links will be intercepted in handleCollectionFilterLinkClick
        }

        /**
         * Handle clicks on collection filter links
         * Intercepts links that match the collection base URL and have filter parameters
         */
        handleCollectionFilterLinkClick(event) {
            const link = event.target.closest('a[href]');
            if (!link) return;
            
            try {
                const linkUrl = new URL(link.href, window.location.origin);
                const linkBaseUrl = linkUrl.pathname;
                
                // Check if this link points to the same collection base URL
                if (linkBaseUrl !== this.collectionBaseUrl) {
                    return;
                }
                
                // Check if the link has the collection filter data attribute enabled
                const hasCollectionFilterAttr = link.hasAttribute('data-collection-filter') && 
                                                link.getAttribute('data-collection-filter') === 'true';
                
                // If the link doesn't have the data attribute, allow normal navigation
                if (!hasCollectionFilterAttr) {
                    return;
                }
                
                // Check if the link has filter parameters
                const hasFilterParams = Array.from(linkUrl.searchParams.keys()).some(key => 
                    key.startsWith('filter.')
                );
                
                // Prevent default navigation (for both links with and without filter params)
                event.preventDefault();
                event.stopPropagation();
                
                // Apply filters from the link URL
                // If no filter params, this will clear all filters
                this.applyFiltersFromURL(linkUrl.toString());
                
            } catch (e) {
                // If URL parsing fails, allow normal navigation
                console.warn('Error processing collection filter link:', e);
            }
        }

        /**
         * Apply filters from a URL to the collection-filters component
         * This replaces all current filters with the ones from the URL
         */
        applyFiltersFromURL(urlString) {
            // Set flag to prevent recursive URL updates
            this.isApplyingFiltersFromURL = true;
            
            try {
                const linkUrl = new URL(urlString, window.location.origin);
                const linkParams = new URLSearchParams(linkUrl.search);
                
                // Build new URL parameters from the link URL
                // This preserves ALL filter parameters from the link, including ones that don't have form inputs
                const newUrlParams = new URLSearchParams();
                
                // Track unique values per parameter to avoid duplicates
                const paramValues = new Map();
                
                // Collect all filter parameters from the link URL (deduplicating)
                for (const [key, value] of linkParams.entries()) {
                    if (key.startsWith('filter.') && value) {
                        if (!paramValues.has(key)) {
                            paramValues.set(key, new Set());
                        }
                        paramValues.get(key).add(value);
                    } else if (key === 'sort_by' && value) {
                        // Handle sort_by separately (single value)
                        paramValues.set(key, new Set([value]));
                    }
                }
                
                // Add deduplicated parameters to newUrlParams
                for (const [key, values] of paramValues.entries()) {
                    if (key === 'sort_by') {
                        // sort_by is a single value
                        const value = Array.from(values)[0];
                        if (value) {
                            newUrlParams.set(key, value);
                        }
                    } else {
                        // Filter parameters can have multiple values
                        Array.from(values).forEach(value => {
                            newUrlParams.append(key, value);
                        });
                    }
                }
                
                // Update the URL directly with the new parameters
                // This ensures all filters from the link are preserved, even if they don't have form inputs
                const currentUrl = new URL(window.location.href);
                currentUrl.search = newUrlParams.toString();
                
                // Update URL using the standardized URL change utility
                if (window.theme && window.theme.utils && window.theme.utils.dispatchUrlChange) {
                    window.theme.utils.dispatchUrlChange('collection-filter', { 
                        url: currentUrl.toString(),
                        urlParameters: newUrlParams.toString(),
                        state: { urlParameters: newUrlParams.toString() },
                        historyAction: 'replace'
                    });
                } else {
                    // Fallback: use pushState directly
                    window.history.replaceState({ urlParameters: newUrlParams.toString() }, '', currentUrl.toString());
                }
                
                // Now update form inputs to match the URL
                // Use a temporary flag to prevent change events from triggering URL updates
                // First, clear all current filters
                const allFilterInputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"], input[type="number"]');
                allFilterInputs.forEach(input => {
                    if (input.type === 'checkbox' || input.type === 'radio') {
                        input.checked = false;
                    } else if (input.type === 'number') {
                        input.value = '';
                    }
                });
                
                // Update the filters array
                this.filters.forEach(filter => {
                    if (filter.type === 'checkbox' || filter.type === 'radio') {
                        filter.element.checked = false;
                        filter.checked = false;
                    } else if (filter.type === 'number') {
                        filter.element.value = '';
                        filter.value = '';
                    }
                });
                
                // Apply filters from the URL to form inputs that exist
                allFilterInputs.forEach(input => {
                    if (input.type === 'checkbox') {
                        const urlValues = linkParams.getAll(input.name);
                        if (urlValues.includes(input.value)) {
                            input.checked = true;
                            // Update the filter in the array
                            const filter = this.filters.find(f => f.element === input);
                            if (filter) {
                                filter.checked = true;
                            }
                        }
                    } else if (input.type === 'radio') {
                        const urlValue = linkParams.get(input.name);
                        if (urlValue === input.value) {
                            input.checked = true;
                            // Update the filter in the array
                            const filter = this.filters.find(f => f.element === input);
                            if (filter) {
                                filter.checked = true;
                            }
                        }
                    } else if (input.type === 'number') {
                        const urlValue = linkParams.get(input.name);
                        if (urlValue) {
                            input.value = urlValue;
                            // Update the filter in the array
                            const filter = this.filters.find(f => f.element === input);
                            if (filter) {
                                filter.value = urlValue;
                            }
                        }
                    }
                });
                
                // Update sort_by if present in URL
                const sortBy = linkParams.get('sort_by');
                if (sortBy) {
                    this.sortBy = sortBy;
                    const sortSelects = this.querySelectorAll('select[name="sort_by"]');
                    sortSelects.forEach(sortSelect => {
                        sortSelect.value = sortBy;
                    });
                }
                
                // Update active values count
                this.updateActiveValuesCount();
                
                // Reset flag before triggering section update
                this.isApplyingFiltersFromURL = false;
                
                // Request update through centralized manager
                filterManager.requestUpdate(this);
                
                // Update active states for filter links
                requestAnimationFrame(() => {
                    this.updateFilterLinkActiveStates();
                });
                
            } catch (e) {
                console.error('Error applying filters from URL:', e);
                this.isApplyingFiltersFromURL = false;
            }
        }

        /**
         * Update active states for collection filter links
         * Compares current URL filter parameters with link filter parameters
         * (ignoring sort_by) and adds/removes the 'active' class
         */
        updateFilterLinkActiveStates() {
            // Get current page filter parameters (excluding sort_by)
            const currentFilterParams = this.getFilterParamsFromURL(window.location.href);
            
            // Find all links that point to the same collection base URL
            const allLinks = document.querySelectorAll('a[href]');
            const collectionFilterLinks = Array.from(allLinks).filter(link => {
                try {
                    const linkUrl = new URL(link.href, window.location.origin);
                    const linkBaseUrl = linkUrl.pathname;
                    
                    // Check if this link points to the same collection base URL
                    if (linkBaseUrl !== this.collectionBaseUrl) {
                        return false;
                    }
                    
                    // Include links that either:
                    // 1. Have the data-collection-filter attribute enabled, OR
                    // 2. Have filter parameters (for backwards compatibility with existing links)
                    const hasCollectionFilterAttr = link.hasAttribute('data-collection-filter') && 
                                                    link.getAttribute('data-collection-filter') === 'true';
                    const hasFilterParams = Array.from(linkUrl.searchParams.keys()).some(key => 
                        key.startsWith('filter.')
                    );
                    
                    return hasCollectionFilterAttr || hasFilterParams;
                } catch (e) {
                    return false;
                }
            });
            
            // Update active state for each link
            collectionFilterLinks.forEach(link => {
                try {
                    const linkFilterParams = this.getFilterParamsFromURL(link.href);
                    
                    // Compare filter parameters (ignoring sort_by)
                    const isActive = this.compareFilterParams(currentFilterParams, linkFilterParams);
                    
                    if (isActive) {
                        link.classList.add('active');
                    } else {
                        link.classList.remove('active');
                    }
                } catch (e) {
                    console.warn('Error updating active state for link:', e);
                }
            });
        }

        // Public API methods
        clearAllFilters() {
            // Clear ALL inputs in the component (not just the ones in filters array)
            const allFilterInputs = this.querySelectorAll('input[type="checkbox"], input[type="radio"], input[type="number"]');
            allFilterInputs.forEach(input => {
                if (input.type === 'checkbox' || input.type === 'radio') {
                    input.checked = false;
                } else if (input.type === 'number') {
                    input.value = '';
                }
            });
            
            // Also update the filters array
            this.filters.forEach(filter => {
                if (filter.type === 'checkbox' || filter.type === 'radio') {
                    filter.element.checked = false;
                    filter.checked = false;
                } else if (filter.type === 'number') {
                    filter.element.value = '';
                    filter.value = '';
                }
            });

            // Reset sort by to default - update ALL sort selects
            const sortSelects = this.querySelectorAll('select[name="sort_by"]');
            sortSelects.forEach(sortSelect => {
                sortSelect.value = sortSelect.dataset.defaultSortBy || '';
            });
            
            if (sortSelects.length > 0) {
                this.sortBy = sortSelects[0].value;
            }

            this.updateActiveValuesCount();
            
            // Clear URL parameters completely
            this.clearURLParameters();
            
            // Request update through centralized manager
            filterManager.requestUpdate(this);
        }

        applyFilters() {
            // This method is kept for backward compatibility but filtering now happens automatically
            // when filters change, so no additional action is needed here
        }

        setFilter(filterName, value) {
            const filter = this.filters.find(f => f.name === filterName);
            if (filter) {
                if (filter.type === 'checkbox') {
                    filter.element.checked = true;
                    filter.element.value = value;
                } else if (filter.type === 'radio') {
                    filter.element.checked = true;
                    filter.element.value = value;
                } else if (filter.type === 'number') {
                    filter.element.value = value;
                }
                
                this.updateActiveValuesCount();
                this.updateFilters();
            }
        }

        removeFilter(filterName, value = null) {
            const matchingFilters = this.filters.filter(f => f.name === filterName);
            
            matchingFilters.forEach(filter => {
                if (value === null || filter.value === value) {
                    if (filter.type === 'checkbox' || filter.type === 'radio') {
                        filter.element.checked = false;
                    } else if (filter.type === 'number') {
                        filter.element.value = '';
                    }
                }
            });

            this.updateActiveValuesCount();
            this.updateFilters();
        }

        setSortBy(sortBy) {
            const sortSelect = this.querySelector('select[name="sort_by"]');
            if (sortSelect) {
                sortSelect.value = sortBy;
                this.sortBy = sortBy;
                this.updateFilters();
            }
        }

        getCurrentFilters() {
            return this.getActiveFilters();
        }

        getCurrentSortBy() {
            return this.sortBy;
        }

        getProductsCount() {
            return this.productsCount;
        }

        getActiveValuesCount() {
            return this.totalActiveValues;
        }

        // Method to handle URL-based filter initialization
        initializeFromURL() {
            const urlParams = new URLSearchParams(window.location.search);
            
            // Update filter states based on URL parameters
            this.filters.forEach(filter => {
                const urlValue = urlParams.get(filter.name);
                
                if (filter.type === 'checkbox' || filter.type === 'radio') {
                    filter.element.checked = urlValue === filter.value;
                    filter.checked = filter.element.checked;
                } else if (filter.type === 'number') {
                    filter.element.value = urlValue || '';
                    filter.value = filter.element.value;
                }
            });
            
            // Update sort by from URL
            const sortBy = urlParams.get('sort_by');
            if (sortBy) {
                this.sortBy = sortBy;
                const sortSelect = this.querySelector('select[name="sort_by"]');
                if (sortSelect) {
                    sortSelect.value = sortBy;
                }
            }

            this.updateActiveValuesCount();
        }
    }

    customElements.define('collection-filters', CollectionFilters);
}
